<template>
  <div class="card card-topo">
    <!-- Título Principal -->
    <div class="card-header bg-light">
      <span class="font-weight-bold text-caption">Profissionais</span>
    </div>

    <div class="card-body">
      <!-- Busca e Adicionar Profissional -->
      <div class="row mb-3">
        <div class="col-md-10 col-12 mb-2">
          <label for="profissional" class="form-label">Profissional</label>
          <vue-select
            id="profissional"
            v-model="profissionalSelecionado"
            :options="profissionaisDisponiveis"
            label="label"
            placeholder="Pesquise por nome, CPF ou CNS"
            :loading="isLoading"
            @open="carregarProfissionais"
            :filterable="true"
            :searchable="true"
            :filter="filtrarProfissionais"
          >
            <template slot="option" slot-scope="option">
              <div>
                <strong>{{ option.nome }}</strong>
                <div class="small-text" v-if="option.no_cbo">
                  CBO: {{ option.no_cbo }}
                </div>
                <div class="small-text" v-if="option.nu_cns">
                  CNS: {{ maskCNS(option.nu_cns) }}
                </div>
                <div class="small-text" v-if="option.nu_ine">
                  INE: {{ option.nu_ine }}
                </div>
              </div>
            </template>
            <div slot="no-options">Não há opções correspondentes</div>
          </vue-select>
        </div>
        <div class="col-md-2 col-12 mb-2 d-flex align-items-end">
          <button
            class="btn btn-primary w-100"
            @click="adicionarProfissional"
            :disabled="!profissionalSelecionado || isLoading"
          >
            <i class="mdi mdi-plus mr-1"></i>
            Adicionar
          </button>
        </div>
      </div>

      <hr />

      <!-- Lista de Profissionais Selecionados -->
      <div class="box" style="margin-top: 0px">
        <v-card class="tabela">
          <v-card-title>
            <v-text-field
              v-model="search"
              append-icon="mdi-magnify"
              label="Buscar profissional"
              class="input-bottom"
              single-line
              hide-details
            ></v-text-field>
          </v-card-title>
          <v-data-table
            @pagination="getPagination"
            :page="currentPage"
            :footer-props="{
              itemsPerPageText: '',
              'items-per-page-options': [10],
              'disable-items-per-page': true,
            }"
            :loading="false"
            loading-text="Carregando, Por favor espere"
            :headers="headers"
            :items="indexedItems"
            :search="search"
            item-key="id"
            class="elevation-1"
          >
            <template v-slot:[`footer.page-text`]="items">
              <span class="paginacao">
                {{ items.pageStart }} - {{ items.pageStop }} de
                {{ items.itemsLength }}
                {{ items.itemsLength === 1 ? 'profissional' : 'profissionais' }}
              </span>
            </template>

            <template v-slot:item.nome="{ item }">
              <div class="font-weight-bold">{{ item.nome }}</div>
              <span v-if="item.isResponsavel" class="badge badge-primary"
                >Responsável</span
              >
            </template>

            <template v-slot:item.no_cbo="{ item }">
              {{ item.no_cbo || '-' }}
            </template>

            <template v-slot:item.no_unidade_saude="{ item }">
              {{ item.no_unidade_saude || '-' }}
            </template>

            <template v-slot:item.nu_ine="{ item }">
              {{ item.nu_ine || '-' }}
            </template>

            <template v-slot:item.acoes="{ item }">
              <div
                class="dropdown"
                :class="{ show: dropdownAberto === item.index }"
                :data-index="item.index"
              >
                <button
                  class="btn btn-sm btn-actions"
                  type="button"
                  @click.stop="toggleDropdown(item.index)"
                  title="Mais opções"
                >
                  <i class="mdi mdi-dots-vertical"></i>
                </button>
                <div
                  class="dropdown-menu dropdown-menu-right"
                  :class="{ show: dropdownAberto === item.index }"
                  :style="{
                    display: dropdownAberto === item.index ? 'block' : 'none',
                  }"
                  @click.stop
                >
                  <a
                    class="dropdown-item"
                    href="#"
                    :class="{
                      'responsavel-disabled':
                        !hasIne(item) || ehProfissionalLogado(item),
                    }"
                    :title="getMotivoResponsavelTooltip(item)"
                    @click.prevent="
                      !hasIne(item) || ehProfissionalLogado(item)
                        ? null
                        : (marcarComoResponsavel(item.index), fecharDropdown())
                    "
                    v-if="!isEditMode && !item.isResponsavel"
                  >
                    <i class="mdi mdi-account-star mr-2"></i>
                    Marcar como responsável
                  </a>
                  <a
                    class="dropdown-item"
                    href="#"
                    @click.prevent="
                      desmarcarComoResponsavel(item.index)
                      fecharDropdown()
                    "
                    v-if="!isEditMode && item.isResponsavel"
                  >
                    <i class="mdi mdi-account-star-outline mr-2"></i>
                    Desmarcar como responsável
                  </a>
                  <a
                    class="dropdown-item text-danger"
                    href="#"
                    @click.prevent="
                      removerProfissional(item.index)
                      fecharDropdown()
                    "
                  >
                    <i class="mdi mdi-delete mr-2"></i>
                    Excluir da lista
                  </a>
                </div>
              </div>
            </template>

            <template v-slot:no-data>
              <div class="alert alert-info mb-0">
                <b>Nenhum profissional adicionado.</b>
              </div>
            </template>

            <template v-slot:no-results>
              <div class="alert alert-info mb-0">
                <b
                  >Nenhuma informação encontrada. Tente outro termo de busca.</b
                >
              </div>
            </template>
          </v-data-table>
        </v-card>
      </div>

      <!-- Mensagem de erro para responsável -->
      <div v-if="erroResponsavel" class="text-danger mt-2 small">
        {{ erroResponsavel }}
      </div>
    </div>
  </div>
</template>

<script>
import { api } from '@/plugins/axios.js'
import VueSelect from 'vue-select'
import 'vue-select/dist/vue-select.css'
import { jwtDecode } from 'jwt-decode'

export default {
  components: {
    'vue-select': VueSelect,
  },
  props: {
    profissionais: {
      type: Array,
      default: () => [],
    },
    isEditMode: {
      type: Boolean,
      default: false,
    },
    erroResponsavel: {
      type: String,
      default: null,
    },
    cpfUsuarioLogado: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      profissionaisLocal: [],
      profissionaisDisponiveis: [],
      profissionalSelecionado: null,
      isLoading: false,
      currentPage: 1,
      search: '',
      dropdownAberto: null,
      headers: [
        {
          text: 'Nome',
          align: 'start',
          filterable: true,
          value: 'nome',
        },
        {
          text: 'CBO',
          align: 'start',
          filterable: false,
          value: 'no_cbo',
        },
        {
          text: 'Unidade de Saúde',
          align: 'start',
          filterable: false,
          value: 'no_unidade_saude',
        },
        {
          text: 'INE',
          align: 'start',
          filterable: false,
          value: 'nu_ine',
        },
        {
          text: 'Ações',
          align: 'center',
          filterable: false,
          value: 'acoes',
          sortable: false,
        },
      ],
    }
  },
  computed: {
    codIbge() {
      // Tenta pegar do store, se não tiver, decodifica o token
      if (this.$store.state.cod_ibge) {
        return this.$store.state.cod_ibge
      }
      if (this.$store.state.accessToken) {
        try {
          const decoded = jwtDecode(this.$store.state.accessToken)
          return decoded.cod_ibge || '0000000'
        } catch (e) {
          return '0000000'
        }
      }
      return '0000000'
    },
    cpfUsuarioLogadoNormalizado() {
      // Prioriza o prop (vindo do index via auth/usuario API), senão tenta o token
      if (this.cpfUsuarioLogado) {
        return String(this.cpfUsuarioLogado).replace(/\D/g, '') || null
      }
      if (this.$store.state.accessToken) {
        try {
          const decoded = jwtDecode(this.$store.state.accessToken)
          return (
            (decoded.nu_cpf || decoded.cpf || '').replace(/\D/g, '') || null
          )
        } catch (e) {
          return null
        }
      }
      return null
    },
    cnsUsuarioLogado() {
      if (this.$store.state.accessToken) {
        try {
          const decoded = jwtDecode(this.$store.state.accessToken)
          return (
            (decoded.nu_cns || decoded.cns || '').replace(/\D/g, '') || null
          )
        } catch (e) {
          return null
        }
      }
      return null
    },
    indexedItems() {
      if (this.profissionaisLocal !== undefined) {
        return this.profissionaisLocal.map((item, index) => {
          return {
            id: index,
            index: index,
            isResponsavel: item.logado === true || item.isResponsavel === true,
            ...item,
          }
        })
      }
      return []
    },
  },
  watch: {
    profissionais: {
      immediate: true,
      handler(newProfissionais) {
        this.profissionaisLocal = [...newProfissionais]
        this.currentPage = 1
      },
      deep: true,
    },
  },
  mounted() {
    // Fecha o dropdown ao clicar fora
    document.addEventListener('click', this.fecharDropdownAoClicarFora)
  },
  beforeDestroy() {
    document.removeEventListener('click', this.fecharDropdownAoClicarFora)
  },
  methods: {
    async carregarProfissionais() {
      if (this.profissionaisDisponiveis.length > 0) {
        return Promise.resolve() // Já carregado
      }

      this.isLoading = true
      try {
        // Novo endpoint informado: /app/profissionais-lotacoes/{cod_ibge}/1
        const response = await api.get(
          `/app/profissionais-lotacoes/${this.codIbge}/1`
        )

        const lista = Array.isArray(response.data) ? response.data : []

        this.profissionaisDisponiveis = lista
          .map((prof) => {
            try {
              // Neste endpoint, as lotações vêm em uma string JSON no campo "lotacoes".
              // Vamos pegar a primeira lotação ativa (ou a primeira da lista).
              let lotacoes = []
              if (prof.lotacoes) {
                try {
                  lotacoes = JSON.parse(prof.lotacoes)
                } catch (e) {}
              }

              const lotacaoAtiva =
                Array.isArray(lotacoes) && lotacoes.length > 0
                  ? lotacoes.find((l) => l.st_ativo === 1) || lotacoes[0]
                  : null

              const nome = prof.no_profissional || 'Profissional'
              const cns =
                prof.nu_cns || (lotacaoAtiva && lotacaoAtiva.nu_cns) || ''
              const cpf =
                prof.nu_cpf || (lotacaoAtiva && lotacaoAtiva.nu_cpf) || ''
              const no_cbo =
                (lotacaoAtiva && lotacaoAtiva.no_cbo) || prof.no_cbo || ''
              const nu_ine = (lotacaoAtiva && lotacaoAtiva.nu_ine) || null
              const nu_cnes = (lotacaoAtiva && lotacaoAtiva.nu_cnes) || null
              const no_unidade_saude =
                (lotacaoAtiva && lotacaoAtiva.no_unidade_saude) || null
              const id_funcao = (lotacaoAtiva && lotacaoAtiva.co_cbo) || null

              const label = `${nome}${
                cns ? ' - CNS: ' + this.maskCNS(cns) : ''
              }${cpf ? ' - CPF: ' + cpf : ''}`

              return {
                nome,
                no_profissional: prof.no_profissional || nome,
                nu_cns: cns || null,
                nu_cpf: cpf || null,
                no_unidade_saude,
                nu_cnes,
                nu_ine,
                id_funcao,
                no_cbo: no_cbo || 'CBO',
                label,
                // Adiciona campos para busca
                searchText: `${nome} ${cns} ${cpf} ${no_cbo}`.toLowerCase(),
              }
            } catch (e) {
              return null
            }
          })
          .filter(Boolean)
      } catch (error) {
        this.profissionaisDisponiveis = []
      } finally {
        this.isLoading = false
      }
    },
    adicionarProfissional() {
      if (!this.profissionalSelecionado) {
        return
      }

      const prof = this.profissionalSelecionado
      const jaExiste = this.profissionaisLocal.some(
        (p) =>
          p.cnsProfissional === prof.nu_cns ||
          (prof.nu_cpf &&
            p.cnsProfissional &&
            p.cnsProfissional.includes(prof.nu_cpf))
      )

      if (!jaExiste) {
        this.profissionaisLocal.push({
          nome: prof.nome || prof.no_profissional || '',
          no_profissional: prof.no_profissional || prof.nome || '',
          nu_cpf: prof.nu_cpf || null,
          cnsProfissional: prof.nu_cns || null,
          nu_cnes: prof.nu_cnes || null,
          no_unidade_saude: prof.no_unidade_saude || null,
          nu_ine: prof.nu_ine || null,
          id_funcao: prof.id_funcao,
          no_cbo: prof.no_cbo,
          selecionado: true,
          logado: false,
        })
        this.$emit('update:profissionais', [...this.profissionaisLocal])
        this.profissionalSelecionado = null
      } else {
        this.profissionalSelecionado = null
      }
    },

    /**
     * Atualiza a página atual da tabela.
     * @param {Object} options Dados da paginação
     */
    getPagination(options) {
      this.currentPage = options.page
    },

    removerProfissional(index) {
      this.profissionaisLocal.splice(index, 1)
      this.$emit('update:profissionais', [...this.profissionaisLocal])
    },

    toggleDropdown(index) {
      if (this.dropdownAberto === index) {
        this.dropdownAberto = null
      } else {
        this.dropdownAberto = index
        // Aguarda o próximo tick para calcular a posição do dropdown
        this.$nextTick(() => {
          this.ajustarPosicaoDropdown(index)
        })
      }
    },

    ajustarPosicaoDropdown(index) {
      // Tenta encontrar o dropdown de várias formas
      let dropdown = this.$el.querySelector(`.dropdown[data-index="${index}"]`)

      if (!dropdown) {
        // Tenta encontrar pelo índice na lista de dropdowns
        const dropdowns = this.$el.querySelectorAll('.dropdown')
        const dropdownArray = Array.from(dropdowns)
        const itemIndex = this.indexedItems.findIndex(
          (item) => item.index === index
        )
        if (itemIndex >= 0 && dropdownArray[itemIndex]) {
          dropdown = dropdownArray[itemIndex]
        }
      }

      if (!dropdown) {
        return
      }

      const button = dropdown.querySelector('button')
      const menu = dropdown.querySelector('.dropdown-menu')

      if (!button || !menu) {
        return
      }

      const rect = button.getBoundingClientRect()

      menu.style.position = 'fixed'
      menu.style.top = `${rect.bottom + 2}px`
      menu.style.right = `${window.innerWidth - rect.right}px`
      menu.style.left = 'auto'
      menu.style.zIndex = '9999'
      menu.style.display = 'block'
      menu.style.visibility = 'visible'
      menu.style.opacity = '1'
    },

    fecharDropdown() {
      this.dropdownAberto = null
    },

    fecharDropdownAoClicarFora(event) {
      // Fecha o dropdown se o clique foi fora do dropdown
      if (this.dropdownAberto !== null) {
        const dropdownElement = event.target.closest('.dropdown')
        if (!dropdownElement) {
          this.fecharDropdown()
        }
      }
    },

    hasIne(item) {
      const ine = item.nu_ine || item.ine || ''
      if (!ine) return false
      const ineTrim = String(ine).trim()
      return ineTrim !== '' && ineTrim !== '-'
    },
    getMotivoResponsavelTooltip(item) {
      if (this.ehProfissionalLogado(item)) {
        return 'Você não pode se marcar como responsável.'
      }
      if (!this.hasIne(item)) {
        return 'Apenas profissionais com INE preenchido podem ser responsáveis.'
      }
      return 'Marcar como responsável'
    },
    ehProfissionalLogado(item) {
      const cpfItem = (item.nu_cpf || item.cpf || '').replace(/\D/g, '')
      const cnsItem = (
        item.nu_cns ||
        item.cnsProfissional ||
        item.cns ||
        ''
      ).replace(/\D/g, '')
      if (
        this.cpfUsuarioLogadoNormalizado &&
        cpfItem &&
        cpfItem === this.cpfUsuarioLogadoNormalizado
      )
        return true
      if (this.cnsUsuarioLogado && cnsItem && cnsItem === this.cnsUsuarioLogado)
        return true
      return false
    },
    marcarComoResponsavel(index) {
      if (index < 0 || index >= this.profissionaisLocal.length) {
        return
      }
      const profissional = this.profissionaisLocal[index]
      if (this.ehProfissionalLogado(profissional)) {
        this.$toast.warning(
          'Você não pode se marcar como responsável.',
          'Aviso',
          {
            position: 'topRight',
            timeout: 4000,
          }
        )
        return
      }

      // Remove a marcação de responsável de todos os profissionais
      this.profissionaisLocal.forEach((prof) => {
        prof.logado = false
        prof.isResponsavel = false
      })

      // Marca o profissional selecionado como responsável
      profissional.logado = true
      profissional.isResponsavel = true

      // Move para o início da lista
      if (index !== 0) {
        this.profissionaisLocal.splice(index, 1)
        this.profissionaisLocal.unshift(profissional)
      }

      this.$emit('update:profissionais', [...this.profissionaisLocal])
    },
    desmarcarComoResponsavel(index) {
      if (index < 0 || index >= this.profissionaisLocal.length) {
        return
      }
      const profissional = this.profissionaisLocal[index]
      profissional.logado = false
      profissional.isResponsavel = false
      this.$emit('update:profissionais', [...this.profissionaisLocal])
    },
    filtrarProfissionais(options, search) {
      if (!search) {
        return options
      }
      const searchLower = search.toLowerCase().replace(/\D/g, '') // Remove caracteres não numéricos para busca
      return options.filter((prof) => {
        // Busca no texto de busca pré-processado
        if (prof.searchText && prof.searchText.includes(search.toLowerCase())) {
          return true
        }
        const nome = (prof.nome || '').toLowerCase()
        const cns = (prof.nu_cns || '').replace(/\D/g, '')
        const cpf = (prof.nu_cpf || '').replace(/\D/g, '')
        const searchClean = search.replace(/\D/g, '')

        return (
          nome.includes(search.toLowerCase()) ||
          (searchClean && cns.includes(searchClean)) ||
          (searchClean && cpf.includes(searchClean))
        )
      })
    },

    maskCNS(cns) {
      if (!cns) return ''
      const digits = cns.replace(/\D/g, '')
      const part1 = digits.substr(0, 3)
      const part2 = digits.substr(3, 3)
      const part3 = digits.substr(6, 4)
      const part4 = digits.substr(10)
      let masked = part1
      if (part2) masked += '.' + part2
      if (part3) masked += '.' + part3
      if (part4) masked += '.' + part4
      return masked
    },
  },
}
</script>

<style scoped>
.header {
  background-color: #f5f5f5;
}

.card-topo {
  padding: 0px;
}

.badge-primary {
  background-color: rgb(15, 175, 215);
  color: white;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 0.75rem;
}

.user-name {
  font-size: 1.1rem;
  color: #333333;
}

.info-text {
  font-size: 0.9rem;
  color: #555555;
}

.small-text {
  font-size: 0.8rem;
  color: #757575;
}

.btn-actions {
  background: transparent;
  border: none;
  color: #6c757d;
  padding: 4px 8px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-actions:hover {
  background: #f8f9fa;
  color: #495057;
  border-radius: 4px;
}

.btn-actions:focus {
  box-shadow: none;
  outline: none;
}

.btn-actions i {
  font-size: 1.2rem;
}

.dropdown.show .dropdown-menu {
  display: block !important;
}

/* Ajustes para dropdown dentro da tabela */
.v-data-table .dropdown {
  position: relative;
}

.v-data-table .dropdown-menu {
  position: fixed !important;
  z-index: 9999 !important;
  margin-top: 0.125rem;
  min-width: 200px;
  display: none !important;
}

.v-data-table .dropdown.show .dropdown-menu,
.v-data-table .dropdown-menu[style*='display: block'] {
  display: block !important;
  visibility: visible !important;
  opacity: 1 !important;
}

/* Garante que a tabela não corte o dropdown */
.v-data-table {
  overflow: visible !important;
}

.v-data-table__wrapper {
  overflow: visible !important;
}

.v-data-table table {
  overflow: visible !important;
}

.v-data-table tbody {
  overflow: visible !important;
}

.v-data-table tbody tr {
  overflow: visible !important;
}

.v-data-table tbody td {
  overflow: visible !important;
  position: relative;
}

.dropdown-item.responsavel-disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-primary {
  background-color: #0fafd7 !important;
  border-color: #0fafd7 !important;
  color: #fff !important;
}

.btn-primary:hover {
  background-color: #0d98bb !important;
  border-color: #0d98bb !important;
  color: #fff !important;
}

.btn-primary:disabled {
  background-color: #6c757d !important;
  border-color: #6c757d !important;
  opacity: 0.65;
}
</style>
